import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { ArrowRight, Crown, TrendingUp } from "lucide-react";
import { LaurelMark, Logo } from "@/components/Logo";
import { AREAS, areaByName } from "@/data/auditData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedResults() {
  const { leadId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const auditLink = `/audit?ref=${leadId}`;

  useEffect(() => {
    axios
      .get(`${API}/results/${leadId}`)
      .then((res) => setData(res.data))
      .catch(() => setError(true));
    const flag = `lov_viewed_${leadId}`;
    if (!sessionStorage.getItem(flag)) {
      sessionStorage.setItem(flag, "1");
      axios.post(`${API}/results/${leadId}/view`).catch(() => {});
    }
  }, [leadId]);

  if (error) {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center" data-testid="shared-results-error">
        <LaurelMark className="w-12 h-12 mb-6" />
        <h1 className="font-serif text-3xl text-burgundy-deep">These results aren't available.</h1>
        <p className="mt-3 text-[#62686E]">The link may be incomplete — but your own check is waiting.</p>
        <Link
          to="/audit"
          data-testid="shared-error-take-audit-btn"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-burgundy px-8 py-4 text-champagne text-sm tracking-widest uppercase hover:bg-burgundy-deep transition-colors"
        >
          Start Your Check <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center" data-testid="shared-results-loading">
        <LaurelMark className="w-10 h-10 animate-pulse" />
      </div>
    );
  }

  const strongestArea = areaByName(data.strongest_area);
  const gapArea = areaByName(data.primary_gap);
  const chartData = AREAS.map((a) => ({ area: a.area, score: data.scores[a.area] ?? 0 }));

  return (
    <div className="min-h-screen bg-ivory" data-testid="shared-results-page">
      <header className="border-b border-burgundy/10 bg-ivory/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="shared-nav-home-link"><Logo /></Link>
          <Link
            to={auditLink}
            data-testid="shared-nav-take-audit-btn"
            className="rounded-full bg-burgundy text-champagne px-6 py-2.5 text-sm tracking-wide hover:bg-burgundy-deep transition-colors"
          >
            Start Your Check
          </Link>
        </div>
      </header>

      <section className="bg-burgundy-deep text-champagne py-20 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs uppercase tracking-[0.35em] text-gold mb-6"
        >
          {data.first_name} took the Lady of Victory® Alignment Check
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl"
          data-testid="shared-overall-score"
        >
          {Math.round(data.overall_result)}<span className="text-gold text-3xl align-top">/100</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mt-4 text-champagne/70 max-w-xl mx-auto text-base sm:text-lg px-6"
        >
          Her self-leadership mirror — strongest in {data.strongest_area}, growing in {data.primary_gap}.
        </motion.p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="h-80 sm:h-96"
          data-testid="shared-radar-chart"
        >
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="72%">
              <PolarGrid stroke="rgba(31,58,95,0.15)" />
              <PolarAngleAxis dataKey="area" tick={{ fill: "#1F3A5F", fontSize: 12, fontFamily: "Plus Jakarta Sans" }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="score" stroke="#1F3A5F" fill="#1F3A5F" fillOpacity={0.25} strokeWidth={2} isAnimationActive />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>

        <div className="grid gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="border border-gold/50 bg-[#F1EFE7] p-8"
            data-testid="shared-strongest-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold-muted mb-4">
              <Crown className="w-4 h-4" /> Her strongest area
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-burgundy-deep">
              {data.strongest_area} · {Math.round(data.scores[data.strongest_area] ?? 0)}%
            </h2>
            <p className="mt-3 text-[#62686E] leading-relaxed">{strongestArea?.affirmation}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="border border-burgundy/25 bg-[#EEF0F2] p-8"
            data-testid="shared-gap-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-burgundy-light mb-4">
              <TrendingUp className="w-4 h-4" /> Her growth area
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-burgundy-deep">
              {data.primary_gap} · {Math.round(data.scores[data.primary_gap] ?? 0)}%
            </h2>
            <p className="mt-3 text-[#62686E] leading-relaxed">{gapArea?.gapExplanation}</p>
          </motion.div>
        </div>
      </section>

      <section className="bg-burgundy-deep text-champagne py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto px-6"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6">Your responsibilities aren't your identity.</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
            Where does your own mirror point?
          </h2>
          <p className="mt-4 text-champagne/70 leading-relaxed">
            Five minutes. Twenty honest questions. Your strongest area, your hidden gap,
            and one intentional step — free.
          </p>
          <Link
            to={auditLink}
            data-testid="shared-take-audit-btn"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-burgundy-deep text-sm font-semibold tracking-widest uppercase hover:bg-champagne transition-colors"
          >
            Discover Your Gap
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </section>

      <footer className="bg-burgundy-deep border-t border-gold/15 py-8 text-center">
        <p className="text-xs text-champagne/40 tracking-wide">
          © {new Date().getFullYear()} Lady of Victory® · Victorious Leading Solutions™
        </p>
      </footer>
    </div>
  );
}
